import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Container,
    Segment,
    Header,
    Dropdown,
    Button,
    Icon,
    Message,
    Table,
} from 'semantic-ui-react';
import { useQuery, gql } from '@apollo/client';
import moment from 'moment';
import { useIsAdmin } from '../../../hooks/isAdmin';
import { useBlocks } from '../../../hooks/useBlocks';
import { GET_TAGS } from '../../../queries/tags';
import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { InterimStates } from '../../../components/InterimStates';
import { CompareGraph, CompareDay } from './CompareGraph';
import styles from './AttendanceCompare.module.css';

const SERIES_COLORS = [
    { id: 'blue',   label: 'Blue',   hex: '#1e88e5' },
    { id: 'green',  label: 'Green',  hex: '#43a047' },
    { id: 'red',    label: 'Red',    hex: '#e53935' },
    { id: 'orange', label: 'Orange', hex: '#fb8c00' },
    { id: 'purple', label: 'Purple', hex: '#8e24aa' },
    { id: 'teal',   label: 'Teal',   hex: '#00897b' },
    { id: 'pink',   label: 'Pink',   hex: '#e91e63' },
    { id: 'indigo', label: 'Indigo', hex: '#3949ab' },
    { id: 'yellow', label: 'Yellow', hex: '#d4a000' },
    { id: 'brown',  label: 'Brown',  hex: '#6d4c41' },
] as const;

const MAX_SERIES = 10;

const GET_BLOCK_TAGS = gql`
  query BlockTagsForCompare {
    blockTags { id name }
  }
`;

const GET_SERIES_DAYS = gql`
  query SeriesDays($blockTagId: Int!, $filters: String) {
    blockAttendanceReport(blockTagId: $blockTagId, filters: $filters) {
      days {
        date
        attendancePercentage
        presentCount
        absentCount
        otherCount
      }
    }
  }
`;

interface SeriesConfig {
    id: string;
    colorId: string;
    tagIds: string[];
}

// Separate component so useQuery is called once per series without breaking hook rules.
const SeriesLoader: React.FC<{
    blockTagId: number;
    filters: string;
    seriesId: string;
    onData: (id: string, days: CompareDay[]) => void;
}> = ({ blockTagId, filters, seriesId, onData }) => {
    const { data } = useQuery(GET_SERIES_DAYS, {
        variables: { blockTagId, filters: filters || '' },
        fetchPolicy: 'cache-first',
    });
    const onDataRef = useRef(onData);
    onDataRef.current = onData;

    useEffect(() => {
        if (data?.blockAttendanceReport?.days) {
            onDataRef.current(seriesId, data.blockAttendanceReport.days);
        }
    }, [data, seriesId]);

    return null;
};

const ColorPicker: React.FC<{
    selectedId: string;
    disabledIds: string[];
    onChange: (id: string) => void;
}> = ({ selectedId, disabledIds, onChange }) => (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
        {SERIES_COLORS.map(c => {
            const isSelected = c.id === selectedId;
            const isDisabled = disabledIds.includes(c.id);
            return (
                <div
                    key={c.id}
                    title={isDisabled ? `${c.label} (in use)` : c.label}
                    onClick={() => !isDisabled && onChange(c.id)}
                    style={{
                        width: 22, height: 22, borderRadius: '50%',
                        background: c.hex,
                        opacity: isDisabled && !isSelected ? 0.2 : 1,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        border: isSelected ? '3px solid #333' : '2px solid rgba(0,0,0,0.1)',
                        boxSizing: 'border-box',
                        flexShrink: 0,
                        transition: 'opacity 0.15s, border-color 0.15s',
                    }}
                />
            );
        })}
    </div>
);

const DateRangeSlider: React.FC<{
    dates: string[];
    startIdx: number;
    endIdx: number;
    onChange: (start: number, end: number) => void;
}> = ({ dates, startIdx, endIdx, onChange }) => {
    if (dates.length < 2) return null;
    const max = dates.length - 1;
    const startPct = (startIdx / max) * 100;
    const endPct = (endIdx / max) * 100;

    return (
        <div style={{ marginTop: '1.5em', marginBottom: '1em' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: '#555' }}>
                <span><strong>From:</strong> {moment(dates[startIdx], 'YYYY-MM-DD').format('D MMM YYYY')}</span>
                <span style={{ color: '#999', fontSize: 11 }}>Date range</span>
                <span><strong>To:</strong> {moment(dates[endIdx], 'YYYY-MM-DD').format('D MMM YYYY')}</span>
            </div>
            <div style={{ position: 'relative', height: 24 }}>
                {/* Base track */}
                <div style={{
                    position: 'absolute', top: '50%', left: 0, right: 0,
                    height: 4, background: '#e0e0e0', transform: 'translateY(-50%)', borderRadius: 2,
                }} />
                {/* Active range highlight */}
                <div style={{
                    position: 'absolute', top: '50%',
                    left: `${startPct}%`,
                    width: `${endPct - startPct}%`,
                    height: 4, background: '#1e88e5', transform: 'translateY(-50%)', borderRadius: 2,
                }} />
                {/* Start handle */}
                <input
                    type='range'
                    className={styles.rangeInput}
                    min={0} max={max}
                    value={startIdx}
                    onChange={e => onChange(Math.min(Number(e.target.value), endIdx), endIdx)}
                />
                {/* End handle */}
                <input
                    type='range'
                    className={styles.rangeInput}
                    min={0} max={max}
                    value={endIdx}
                    onChange={e => onChange(startIdx, Math.max(Number(e.target.value), startIdx))}
                />
            </div>
        </div>
    );
};

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface GraphSeriesItem {
    id: string;
    label: string;
    color: string;
    days: CompareDay[];
}

const DayOfWeekTable: React.FC<{ series: GraphSeriesItem[] }> = ({ series }) => {
    const todayKey = moment().format('YYYY-MM-DD');

    // isoWeekday(): 1 = Mon … 7 = Sun → map to index 0–6
    // Only count days strictly before today, matching the block stats attendance rate logic.
    const rows = series.map(s => {
        const buckets: number[][] = Array.from({ length: 7 }, () => []);
        s.days
            .filter(d => d.date < todayKey)
            .forEach(d => {
                const dow = moment(d.date, 'YYYY-MM-DD').isoWeekday() - 1;
                buckets[dow].push(d.attendancePercentage);
            });
        const avgs = buckets.map(bucket =>
            bucket.length > 0 ? bucket.reduce((a, b) => a + b, 0) / bucket.length : null
        );
        return { id: s.id, label: s.label, color: s.color, avgs };
    });

    return (
        <div style={{ overflowX: 'auto' }}>
            <Table celled compact textAlign='center'>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell textAlign='left'>Series</Table.HeaderCell>
                        {DOW_LABELS.map(d => (
                            <Table.HeaderCell key={d}>{d}</Table.HeaderCell>
                        ))}
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {rows.map(row => (
                        <Table.Row key={row.id}>
                            <Table.Cell textAlign='left'>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div style={{
                                        width: 12, height: 12, borderRadius: '50%',
                                        background: row.color, flexShrink: 0,
                                    }} />
                                    <span>{row.label}</span>
                                </div>
                            </Table.Cell>
                            {row.avgs.map((avg, i) => (
                                <Table.Cell key={i} style={{ color: avg == null ? '#bbb' : undefined }}>
                                    {avg == null ? 'N/A' : `${Math.round(avg)}%`}
                                </Table.Cell>
                            ))}
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </div>
    );
};

const AttendanceCompare = () => {
    const isAdmin = useIsAdmin();
    const { currentBlockTagId } = useBlocks();

    const [blockTagId, setBlockTagId] = useState<number | null>(null);
    const [series, setSeries] = useState<SeriesConfig[]>([
        { id: '1', colorId: 'blue',  tagIds: [] },
        { id: '2', colorId: 'green', tagIds: [] },
    ]);
    const [seriesData, setSeriesData] = useState<Record<string, CompareDay[]>>({});
    const [rangeStart, setRangeStart] = useState(0);
    const [rangeEnd, setRangeEnd] = useState(0);

    const selectedBlockTagId = blockTagId ?? currentBlockTagId;

    const { loading: blocksLoading, error: blocksError, data: blocksData } = useQuery(GET_BLOCK_TAGS);
    const { data: tagsData } = useQuery(GET_TAGS, {
        variables: { showPeopleTags: false, showCourseTags: true },
    });

    // Union of all dates across every loaded series, sorted ascending.
    const allDates = useMemo(() =>
        Array.from(new Set(
            Object.values(seriesData).flatMap(days => days.map(d => d.date))
        )).sort(),
    [seriesData]);

    // Reset range to cover all dates whenever the set of available dates grows or shrinks.
    useEffect(() => {
        setRangeStart(0);
        setRangeEnd(Math.max(0, allDates.length - 1));
    }, [allDates.length]);

    const usedColorIds = series.map(s => s.colorId);

    const handleSeriesData = useCallback((id: string, days: CompareDay[]) => {
        setSeriesData(prev => ({ ...prev, [id]: days }));
    }, []);

    const addSeries = () => {
        if (series.length >= MAX_SERIES) return;
        const unusedColor = SERIES_COLORS.find(c => !usedColorIds.includes(c.id));
        setSeries(prev => [...prev, {
            id: String(Date.now()),
            colorId: unusedColor?.id ?? SERIES_COLORS[0].id,
            tagIds: [],
        }]);
    };

    const removeSeries = (id: string) => {
        setSeries(prev => prev.filter(s => s.id !== id));
        setSeriesData(prev => { const n = { ...prev }; delete n[id]; return n; });
    };

    const updateSeries = (id: string, changes: Partial<SeriesConfig>) => {
        setSeries(prev => prev.map(s => s.id === id ? { ...s, ...changes } : s));
    };

    if (blocksLoading || blocksError) {
        return <InterimStates loading={blocksLoading} error={blocksError} label='blocks' />;
    }
    if (!isAdmin) return null;

    const tagOptions = (tagsData?.tags ?? []).map((t: any) => ({
        key: t.id,
        text: t.name,
        value: `tagId:${t.id}`,
    }));

    const sortedBlocks = [...(blocksData?.blockTags ?? [])].sort((a: any, b: any) => a.id - b.id);

    // Build per-series data, applying the date range filter.
    const startDate = allDates[rangeStart];
    const endDate = allDates[rangeEnd];

    const graphSeries = series.map(s => {
        const colorDef = SERIES_COLORS.find(c => c.id === s.colorId)!;
        const tagNames = s.tagIds.map(tid => {
            const tag = (tagsData?.tags ?? []).find((t: any) => `tagId:${t.id}` === tid);
            return tag?.name ?? tid;
        });
        const days = (seriesData[s.id] ?? []).filter(d =>
            (!startDate || d.date >= startDate) && (!endDate || d.date <= endDate)
        );
        return {
            id: s.id,
            label: tagNames.length > 0 ? tagNames.join(' + ') : 'All courses',
            color: colorDef.hex,
            days,
        };
    });

    const hasData = graphSeries.some(s => s.days.length > 0);

    return (
        <ResponsiveContainer>
            <Segment style={{ padding: '2em 0em' }} vertical>
                <Container>
                    <Header as='h1' textAlign='center'>Attendance Compare</Header>

                    <Dropdown
                        placeholder='Select block'
                        fluid selection search
                        value={selectedBlockTagId ?? undefined}
                        onChange={(_, { value }) => setBlockTagId(value as number)}
                        options={sortedBlocks.map((b: any) => ({ key: b.id, text: b.name, value: b.id }))}
                    />

                    <div style={{ marginTop: '1.5em' }}>
                        {series.map((s, idx) => {
                            const otherColorIds = usedColorIds.filter(id => id !== s.colorId);
                            return (
                                <div
                                    key={s.id}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 12,
                                        marginBottom: 12, flexWrap: 'wrap',
                                    }}
                                >
                                    <span style={{ color: '#888', fontSize: 13, minWidth: 58 }}>
                                        Series {idx + 1}
                                    </span>
                                    <ColorPicker
                                        selectedId={s.colorId}
                                        disabledIds={otherColorIds}
                                        onChange={colorId => updateSeries(s.id, { colorId })}
                                    />
                                    <div style={{ flex: 1, minWidth: 200 }}>
                                        <Dropdown
                                            placeholder='Filter by tags — leave empty for all courses'
                                            fluid multiple selection search
                                            options={tagOptions}
                                            value={s.tagIds}
                                            onChange={(_, { value }) =>
                                                updateSeries(s.id, { tagIds: value as string[] })
                                            }
                                        />
                                    </div>
                                    {series.length > 1 && (
                                        <Button icon basic size='small' onClick={() => removeSeries(s.id)}>
                                            <Icon name='times' />
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {series.length < MAX_SERIES && (
                        <Button basic size='small' onClick={addSeries} style={{ marginBottom: '1em' }}>
                            + Add series
                        </Button>
                    )}

                    {allDates.length >= 2 && (
                        <DateRangeSlider
                            dates={allDates}
                            startIdx={rangeStart}
                            endIdx={rangeEnd}
                            onChange={(start, end) => {
                                setRangeStart(start);
                                setRangeEnd(end);
                            }}
                        />
                    )}

                    {selectedBlockTagId == null && (
                        <Message info content='Select a block above to load attendance data.' />
                    )}
                </Container>

                {selectedBlockTagId != null && series.map(s => (
                    <SeriesLoader
                        key={s.id}
                        blockTagId={selectedBlockTagId}
                        filters={s.tagIds.join(',')}
                        seriesId={s.id}
                        onData={handleSeriesData}
                    />
                ))}

                <div style={{ marginTop: '1em' }}>
                    {hasData ? (
                        <CompareGraph series={graphSeries} />
                    ) : selectedBlockTagId != null ? (
                        <Container>
                            <Message icon='circle notched' content='Loading attendance data...' />
                        </Container>
                    ) : null}
                </div>

                {hasData && (
                    <Container style={{ marginTop: '1.5em' }}>
                        <DayOfWeekTable series={graphSeries} />
                    </Container>
                )}
            </Segment>
        </ResponsiveContainer>
    );
};

export { AttendanceCompare };
